import { Avatar, Button, DropdownMenu, Skeleton } from "@/components/ui";
import { PlayerAccount, WebComponents } from "@/interfaces";
import { extractInitials, fullNameFrom, useAccountSettings, useSocialCoachSession } from "@/utils";
import React from "react";
import { SocialCoachSessionService } from "@/services";
import { toast } from "sonner";
import { Settings, Key, Pencil, Users2, Hand } from "lucide-react";
import { PlayerAliasesDialog } from "./PlayerAliasesDialog";
import EditProfileDialog from "@/containers/Coach/EditPlayer/components/EditProfileDialog";

interface Props {
  playerAccount?: PlayerAccount;
  fetching: boolean;
  clearCaches: () => void;
  reloadPlayer: () => void;
}

export function PlayerProfile({ playerAccount, fetching, clearCaches, reloadPlayer }: Props) {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.PLAYERS);
  const [isEditingProfile, setIsEditingProfile] = React.useState(false);
  const [showAliasesModal, setShowAliasesModal] = React.useState(false);
  const withFullName = Boolean(playerAccount?.firstName || playerAccount?.lastName);
  const [loading, setLoading] = React.useState<boolean>(false);

  const resetPwd = (): void => {
    if (!playerAccount) return;
    setLoading(true);
    SocialCoachSessionService.resetPasswordRequest(playerAccount.email!)
      .then(() =>
        toast.success(
          "Success! This Player will receive an email shortly with instructions for resetting their password"
        )
      )
      .finally(() => setLoading(false));
  };

  const sendWelcome = (): void => {
    if (!playerAccount) return;
    setLoading(true);
    SocialCoachSessionService.sendWelcomeEmail(coachId!, playerAccount.userId)
      .then(() =>
        toast.success(
          "Success! This Player will receive an email shortly with instructions for resetting their password"
        )
      )
      .finally(() => setLoading(false));
  };

  const handleCopyEmail = () => {
    if (playerAccount?.email) {
      navigator.clipboard
        .writeText(playerAccount.email)
        .then(() => toast.success("Email copied to clipboard!"))
        .catch(() => toast.error("Failed to copy email to clipboard."));
    }
  };

  return (
    <>
      <div className="flex items-center gap-4 w-full">
        <Skeleton loading={fetching} className="rounded-full shrink-0">
          <Avatar size="xl">
            <Avatar.Image src={playerAccount?.photoUrl} />
            <Avatar.Fallback className="text-xl">
              {extractInitials(playerAccount?.firstName, playerAccount?.lastName)}
            </Avatar.Fallback>
          </Avatar>
        </Skeleton>

        <div className="flex flex-col text-slate-800 flex-1 min-w-0">
          <div className="flex items-center">
            <Skeleton loading={fetching} className="rounded-md w-fit">
              <span className="font-semibold text-lg truncate">
                {playerAccount && withFullName
                  ? fullNameFrom(playerAccount)
                  : fetching
                  ? "Loading..."
                  : "No Name Provided"}
              </span>
              {!fetching && playerAccount && (
                <DropdownMenu>
                  <DropdownMenu.Trigger
                    render={props => (
                      <Button
                        {...props}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-800"
                      >
                        <span className="sr-only">Open menu</span>
                        <Settings className="h-4 w-4" />
                      </Button>
                    )}
                  />
                  <DropdownMenu.Content>
                    {withWritePermission && (
                      <>
                        <DropdownMenu.Item onClick={() => setIsEditingProfile(true)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          <span>Edit Profile</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item onClick={() => setShowAliasesModal(true)}>
                          <Users2 className="mr-2 h-4 w-4" />
                          <span>Edit Aliases</span>
                        </DropdownMenu.Item>
                      </>
                    )}
                    {withWritePermission && <DropdownMenu.Separator />}
                    <DropdownMenu.Item onClick={resetPwd}>
                      <Key className="mr-2 h-4 w-4" />
                      <span>Send Password Reset</span>
                    </DropdownMenu.Item>
                    {!playerAccount.latestLoginDate && (
                      <DropdownMenu.Item onClick={sendWelcome}>
                        <Hand className="mr-2 h-4 w-4" />
                        <span>Send Welcome Email</span>
                      </DropdownMenu.Item>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu>
              )}
            </Skeleton>
          </div>

          <div className="flex items-center gap-2 text-slate-800 mt-1">
            <Skeleton loading={fetching} className="rounded-md w-fit max-w-full">
              <Button
                onClick={handleCopyEmail}
                title="Copy to clipboard"
                className="p-0 m-0 h-fit rounded-none hover:underline text-left cursor-pointer transition-colors text-slate-700 hover:text-slate-600 truncate max-w-full"
                disabled={fetching || !playerAccount?.email}
                variant="link"
              >
                {playerAccount?.email || (fetching ? "player.email@placeholder.com" : "No Email Provided")}
              </Button>
            </Skeleton>
          </div>
        </div>
      </div>

      {isEditingProfile && playerAccount && (
        <EditProfileDialog
          playerAccount={playerAccount}
          open={isEditingProfile}
          onOpenChange={setIsEditingProfile}
          clearCaches={clearCaches}
          reloadPlayer={reloadPlayer}
        />
      )}

      {withWritePermission && (
        <PlayerAliasesDialog
          open={showAliasesModal}
          playerId={playerAccount?.playerId!}
          onOpenChange={setShowAliasesModal}
        />
      )}
    </>
  );
}
